export type ClassTimingStatus = "upcoming" | "live" | "past";

export function getAdminTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

interface ZonedParts {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
}

function getZonedParts(date: Date, timeZone: string): ZonedParts {
    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const read = (type: Intl.DateTimeFormatPartTypes): number => {
        const value = parts.find((part) => part.type === type)?.value || "0";
        return Number(value);
    };

    return {
        year: read("year"),
        month: read("month"),
        day: read("day"),
        hour: read("hour") % 24,
        minute: read("minute"),
    };
}

function zonedLocalToUtc(datePart: string, timePart: string, timeZone: string): Date {
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute] = timePart.split(":").map(Number);
    let utcMs = Date.UTC(year, month - 1, day, hour, minute, 0);

    for (let attempt = 0; attempt < 4; attempt += 1) {
        const zoned = getZonedParts(new Date(utcMs), timeZone);
        const desiredMs = Date.UTC(year, month - 1, day, hour, minute, 0);
        const actualMs = Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, 0);
        utcMs += desiredMs - actualMs;
    }

    return new Date(utcMs);
}

export function localScheduleToUtcPayload(
    datePart: string,
    startTime: string,
    endTime: string,
    timeZone: string,
): { class_date: string; end_date: string; end_time: string } {
    const start = zonedLocalToUtc(datePart, startTime, timeZone);
    const end = zonedLocalToUtc(datePart, endTime, timeZone);

    return {
        class_date: start.toISOString(),
        end_date: end.toISOString(),
        end_time: endTime.length === 5 ? `${endTime}:00` : endTime,
    };
}

export function utcIsoToLocalDateAndTime(iso: string, timeZone: string): { date: string; time: string } {
    const date = new Date(iso);
    const parts = getZonedParts(date, timeZone);
    const datePart = `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
    const timePart = `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`;

    return { date: datePart, time: timePart };
}

export function getClassTimingStatus(classDateIso: string, endDateIso: string, nowMs = Date.now()): ClassTimingStatus {
    const startMs = new Date(classDateIso).getTime();
    const endMs = new Date(endDateIso).getTime();

    if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
        return "past";
    }

    if (nowMs < startMs) {
        return "upcoming";
    }

    if (nowMs <= endMs) {
        return "live";
    }

    return "past";
}

export function isClassPastByEnd(endDateIso: string, nowMs = Date.now()): boolean {
    const endMs = new Date(endDateIso).getTime();
    return !Number.isNaN(endMs) && nowMs > endMs;
}

export function formatClassDate(iso: string, options?: Intl.DateTimeFormatOptions): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return "-";
    }

    return date.toLocaleDateString(undefined, options ?? {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

export function formatClassTimeOnly(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return "-";
    }

    return date.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
    });
}

export function formatClassDateTimeWithZone(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return "-";
    }

    try {
        // dateStyle/timeStyle cannot be combined with timeZoneName (throws RangeError in Chrome).
        return new Intl.DateTimeFormat(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            timeZoneName: "short",
        }).format(date);
    } catch {
        return date.toLocaleString();
    }
}

export function validateScheduleInputs(
    name: string,
    url: string,
    datePart: string,
    startTime: string,
    endTime: string,
    planIds: number[],
): string | null {
    if (!name.trim()) {
        return "Class name is required.";
    }

    if (!url.trim()) {
        return "Live class URL is required.";
    }

    try {
        // eslint-disable-next-line no-new
        new URL(url.trim());
    } catch {
        return "Live class URL must be valid.";
    }

    if (!datePart || !startTime || !endTime) {
        return "Start date, start time, and end time are required.";
    }

    if (!planIds.length) {
        return "Select at least one plan.";
    }

    const start = zonedLocalToUtc(datePart, startTime, getAdminTimezone());
    const end = zonedLocalToUtc(datePart, endTime, getAdminTimezone());

    if (end.getTime() <= start.getTime()) {
        return "End time must be after the start time.";
    }

    return null;
}
